import React, { useState } from 'react'
import { ErrorBoundary } from '@payroll/components/ErrorBoundary'
import { generateMailBITPND50 } from '@payroll/utils/pnd50-pdf'
import { useQuery } from '@tanstack/react-query'
import {
  FileText,
  Download,
  FileSpreadsheet,
  FileDown,
  Printer,
  BarChart3,
  Building2,
  User,
} from 'lucide-react'
import { api } from '@payroll/utils/api'
import { formatCurrency, formatDate, formatNumber } from '@payroll/utils/format'
import type {
  Employee,
  PayrollPeriod,
  PayrollEntry,
  QuickBooksJournalEntry,
  PND1Entry,
  WHTCertificate,
} from '@payroll/types'
import { MONTHS_EN, COMPANY_TAX_IDS } from '@payroll/types'
import { Button } from '@payroll/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@payroll/components/ui/card'
import { Badge } from '@payroll/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@payroll/components/ui/table'
import { Input } from '@payroll/components/ui/input'
import { Select } from '@payroll/components/ui/select'

function toBE(ce: number): number { return ce + 543 }
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@payroll/components/ui/tabs'

// ---------------------------------------------------------------------------
// CSV Export Helper
// ---------------------------------------------------------------------------

function downloadCSV(data: Record<string, unknown>[], filename: string) {
  if (data.length === 0) return

  const headers = Object.keys(data[0])
  const csvRows: string[] = []

  // Add BOM for UTF-8 (needed for Thai characters in Excel)
  csvRows.push(headers.join(','))

  for (const row of data) {
    const values = headers.map((h) => {
      const val = row[h]
      const str = val === null || val === undefined ? '' : String(val)
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    })
    csvRows.push(values.join(','))
  }

  const csvString = '\uFEFF' + csvRows.join('\n')
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Monthly Summary Tab
// ---------------------------------------------------------------------------

function MonthlySummaryTab() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const [year, setYear] = useState(currentYear)
  const [month, setMonth] = useState(currentMonth)

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  const { data: response, isLoading } = useQuery({
    queryKey: ['report-summary', year, month],
    queryFn: () => api.get<any>(`/reports/summary/${year}/${month}`),
  })

  const raw = response?.data
  const summary = raw?.summary
  const noData = !raw || !summary

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Year:</label>
        <Select className="w-28" value={year.toString()} onChange={(e) => setYear(parseInt(e.target.value))}>
          {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
        </Select>
        <label className="text-sm font-medium text-gray-700">Month:</label>
        <Select className="w-44" value={month.toString()} onChange={(e) => setMonth(parseInt(e.target.value))}>
          {MONTHS_EN.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
        </Select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1a237e] border-t-transparent" />
        </div>
      )}

      {noData && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            No payroll data for {MONTHS_EN[month - 1]} {year}. Create and finalize a payroll period first.
          </CardContent>
        </Card>
      )}

      {summary && (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
          <Card><CardContent className="pt-6">
            <p className="text-xs font-medium text-gray-500 uppercase">Employees</p>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(summary.employee_count)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs font-medium text-gray-500 uppercase">Total Income</p>
            <p className="text-2xl font-bold text-[#1a237e]">{formatCurrency(summary.total_income)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs font-medium text-gray-500 uppercase">Total Deductions</p>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary.total_deduction)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs font-medium text-gray-500 uppercase">Income Tax</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_income_tax)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs font-medium text-gray-500 uppercase">Social Security</p>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(summary.total_ss_employee)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-xs font-medium text-gray-500 uppercase">Net Pay</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_net_pay)}</p>
          </CardContent></Card>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// QuickBooks JV Export Tab
// ---------------------------------------------------------------------------

function QuickBooksTab() {
  const { data: periodsResponse } = useQuery({
    queryKey: ['payroll-periods'],
    queryFn: () => api.get<PayrollPeriod[]>('/payroll/periods'),
  })

  const periods = periodsResponse?.data ?? []
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')

  // Auto-select first period when loaded
  React.useEffect(() => {
    if (periods.length > 0 && !selectedPeriodId) {
      setSelectedPeriodId(periods[0].id)
    }
  }, [periods, selectedPeriodId])

  const { data: jvResponse, isLoading } = useQuery({
    queryKey: ['report-quickbooks', selectedPeriodId],
    queryFn: () => api.get<QuickBooksJournalEntry[]>(`/reports/quickbooks/${selectedPeriodId}`),
    enabled: !!selectedPeriodId,
  })

  const jvData = jvResponse?.data as any
  const entries: QuickBooksJournalEntry[] = jvData?.journal_entries ?? jvData ?? []

  const totalDebits = Array.isArray(entries) ? entries.reduce((sum: number, e: any) => sum + (e.debit || 0), 0) : 0
  const totalCredits = Array.isArray(entries) ? entries.reduce((sum: number, e: any) => sum + (e.credit || 0), 0) : 0
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01

  const handleExportCSV = () => {
    const csvData = entries.map((e) => ({
      Account: e.account,
      Debit: e.debit.toFixed(2),
      Credit: e.credit.toFixed(2),
    }))
    // Add totals row
    csvData.push({ 
      Account: 'TOTAL',
      Debit: totalDebits.toFixed(2),
      Credit: totalCredits.toFixed(2),
    })

    const period = periods.find((p) => p.id === selectedPeriodId)
    const filename = period
      ? `quickbooks_jv_${period.year}_${String(period.month).padStart(2, '0')}.csv`
      : 'quickbooks_jv.csv'
    downloadCSV(csvData, filename)
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Period:</label>
        <Select
          className="w-72"
          value={selectedPeriodId}
          onChange={(e) => setSelectedPeriodId(e.target.value)}
        >
          <option value="">Select a period</option>
          {periods.map((p) => (
            <option key={p.id} value={p.id}>
              {MONTHS_EN[p.month - 1]} {p.year} ({p.status})
            </option>
          ))}
        </Select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1a237e] border-t-transparent" />
        </div>
      )}

      {selectedPeriodId && !isLoading && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Journal Entry</CardTitle>
                <CardDescription>QuickBooks-compatible journal voucher</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleExportCSV} disabled={entries.length === 0}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
                <Button size="sm" variant="outline" disabled>
                  <FileDown className="mr-2 h-4 w-4" />
                  Export PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                      No journal entries for this period
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {entries.map((entry, i) => (
                      <TableRow key={i}>
                        <TableCell className={entry.credit > 0 ? 'pl-8' : 'font-medium'}>
                          {entry.account}
                        </TableCell>
                        <TableCell className="text-right">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : ''}
                        </TableCell>
                        <TableCell className="text-right">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals */}
                    <TableRow className="border-t-2 border-gray-300 font-bold">
                      <TableCell className="font-bold">TOTAL</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalDebits)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalCredits)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>

            {entries.length > 0 && (
              <div className="mt-4">
                <Badge variant={isBalanced ? 'default' : 'destructive'}>
                  {isBalanced ? 'Balanced' : `Out of balance by ${formatCurrency(Math.abs(totalDebits - totalCredits))}`}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PND.1 / Monthly Tax Tab
// ---------------------------------------------------------------------------

function PND1Tab() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const [year, setYear] = useState(currentYear)
  const [month, setMonth] = useState(currentMonth)

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  const { data: response, isLoading } = useQuery({
    queryKey: ['report-pnd1', year, month],
    queryFn: () => api.get<any>(`/reports/pnd1/${year}/${month}`),
  })

  const raw = response?.data
  const apiError = response && !response.success ? response.error : null
  const entries: PND1Entry[] = raw?.entries ?? (Array.isArray(raw) ? raw : [])
  const totalIncome = entries.reduce((sum: number, e: any) => sum + (e.income || 0), 0)
  const totalTax = entries.reduce((sum: number, e: any) => sum + (e.tax_withheld || 0), 0)

  const handleExportCSV = () => {
    const csvData = entries.map((e) => ({
      'Employee Name': e.employee_name,
      'National ID': e.national_id,
      Income: e.income.toFixed(2),
      'Tax Withheld': e.tax_withheld.toFixed(2),
    }))
    csvData.push({ 
      'Employee Name': 'TOTAL',
      'National ID': '',
      Income: totalIncome.toFixed(2),
      'Tax Withheld': totalTax.toFixed(2),
    })
    downloadCSV(csvData, `pnd1_${year}_${String(month).padStart(2, '0')}.csv`)
  }

  return (
    <div className="space-y-6">
      {/* Selectors */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Year:</label>
        <Select
          className="w-28"
          value={year.toString()}
          onChange={(e) => setYear(parseInt(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
        <label className="text-sm font-medium text-gray-700">Month:</label>
        <Select
          className="w-44"
          value={month.toString()}
          onChange={(e) => setMonth(parseInt(e.target.value))}
        >
          {MONTHS_EN.map((m, i) => (
            <option key={i} value={i + 1}>
              {m}
            </option>
          ))}
        </Select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1a237e] border-t-transparent" />
        </div>
      )}

      {!isLoading && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  PND.1 Monthly Tax Report
                </CardTitle>
                <CardDescription>
                  {MONTHS_EN[month - 1]} {year} - Tax withheld from employee salary
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={handleExportCSV} disabled={entries.length === 0}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Employee Name</TableHead>
                  <TableHead>National ID</TableHead>
                  <TableHead className="text-right">Income</TableHead>
                  <TableHead className="text-right">Tax Withheld</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className={`text-center py-8 ${apiError ? 'text-red-500' : 'text-gray-500'}`}>
                      {apiError || `No payroll data for ${MONTHS_EN[month - 1]} ${year}. Create and finalize a payroll period first.`}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {entries.map((entry: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="text-gray-500">{i + 1}</TableCell>
                        <TableCell className="font-medium">{entry.employee_name || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.national_id || '-'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.income)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.tax_withheld)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                      <TableCell colSpan={3} className="font-bold">
                        Total ({entries.length} employees)
                      </TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalIncome)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalTax)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PND.1Kor / Annual Tax Tab
// ---------------------------------------------------------------------------

interface PND1KorEntry {
  employee_name: string
  national_id: string
  annual_income: number
  annual_tax: number
  months_worked: number
}

function PND1KorTab() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  const { data: response, isLoading } = useQuery({
    queryKey: ['report-pnd1kor', year],
    queryFn: () => api.get<any>(`/reports/pnd1kor/${year}`),
  })

  const raw = response?.data
  const entries: any[] = raw?.employees ?? (Array.isArray(raw) ? raw : [])
  const totalIncome = entries.reduce((sum: number, e: any) => sum + (e.ytd_income || e.annual_income || 0), 0)
  const totalTax = entries.reduce((sum: number, e: any) => sum + (e.ytd_tax || e.annual_tax || 0), 0)

  const handleExportCSV = () => {
    const csvData = entries.map((e: any) => ({
      'Employee Code': e.employee_code || '',
      'Employee Name': e.first_name_en && e.last_name_en ? `${e.first_name_en} ${e.last_name_en}` : `${e.first_name_th || ''} ${e.last_name_th || ''}`,
      'National ID': e.national_id || '',
      'Months Worked': e.months_worked || e.months_count || 0,
      'Annual Income': (e.annual_income || e.ytd_income || 0).toFixed(2),
      'Annual Tax': (e.annual_tax || e.ytd_tax || 0).toFixed(2),
    }))
    csvData.push({ 'Employee Code': '',
      'Employee Name': 'TOTAL',
      'National ID': '',
      'Months Worked': 0 as unknown as number,
      'Annual Income': totalIncome.toFixed(2),
      'Annual Tax': totalTax.toFixed(2),
    })
    downloadCSV(csvData as unknown as Record<string, unknown>[], `pnd1kor_${year}.csv`)
  }

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Year:</label>
        <Select
          className="w-28"
          value={year.toString()}
          onChange={(e) => setYear(parseInt(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1a237e] border-t-transparent" />
        </div>
      )}

      {!isLoading && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  PND.50 Annual Tax Report
                </CardTitle>
                <CardDescription>
                  Year {year} - Annual summary of employee income and tax withheld
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={handleExportCSV} disabled={entries.length === 0}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Employee Name</TableHead>
                  <TableHead>National ID</TableHead>
                  <TableHead className="text-right">Months</TableHead>
                  <TableHead className="text-right">Annual Income</TableHead>
                  <TableHead className="text-right">Annual Tax</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No data for year {year}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {entries.map((entry: any, i: number) => {
                      const name = entry.first_name_en && entry.last_name_en
                        ? `${entry.first_name_en} ${entry.last_name_en}`
                        : entry.first_name_th && entry.last_name_th
                        ? `${entry.first_name_th} ${entry.last_name_th}`
                        : entry.employee_name || '-'
                      return (
                      <TableRow key={i}>
                        <TableCell className="text-gray-500">{i + 1}</TableCell>
                        <TableCell className="font-medium">{name}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.national_id || '-'}</TableCell>
                        <TableCell className="text-right">{entry.months_worked || entry.months_count || '-'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.annual_income || entry.ytd_income)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.annual_tax || entry.ytd_tax)}</TableCell>
                      </TableRow>
                      )
                    })}
                    {/* Totals Row */}
                    <TableRow className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                      <TableCell colSpan={4} className="font-bold">
                        Total ({entries.length} employees)
                      </TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalIncome)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalTax)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// WHT Certificate Tab
// ---------------------------------------------------------------------------

function WHTCertificateTab() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('')
  const [certNumber, setCertNumber] = useState(`MB${currentYear + 543}/`)

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  const { data: employeesResponse } = useQuery({
    queryKey: ['employees-list'],
    queryFn: () => api.get<Employee[]>('/employees'),
  })

  const employees = employeesResponse?.data ?? []

  // Auto-select first employee
  React.useEffect(() => {
    if (employees.length > 0 && !selectedEmployeeId) {
      setSelectedEmployeeId(employees[0].id)
    }
  }, [employees, selectedEmployeeId])

  const { data: certResponse, isLoading } = useQuery({
    queryKey: ['report-wht-cert', selectedEmployeeId, year],
    queryFn: () => api.get<WHTCertificate>(`/reports/wht-cert/${selectedEmployeeId}/${year}`),
    enabled: !!selectedEmployeeId,
  })

  const selectedEmployee = employees.find((e: any) => e.id === selectedEmployeeId)

  const certRaw = certResponse?.data as any
  const cert = certRaw ? {
    employee: certRaw.employee ?? selectedEmployee ?? {},
    months: certRaw.months ?? [],
    total_income: certRaw.total_income ?? 0,
    total_tax: certRaw.total_tax ?? 0,
  } : null

  return (
    <div className="space-y-6">
      {/* Selectors */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Employee:</label>
        <Select
          className="w-72"
          value={selectedEmployeeId}
          onChange={(e) => setSelectedEmployeeId(e.target.value)}
        >
          <option value="">Select an employee</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {emp.employee_code} - {emp.nickname ? `"${emp.nickname}" ` : ''}{emp.first_name_en || emp.first_name_th} {emp.last_name_en || emp.last_name_th}
            </option>
          ))}
        </Select>
        <label className="text-sm font-medium text-gray-700">Year:</label>
        <Select
          className="w-28"
          value={year.toString()}
          onChange={(e) => {
            const y = parseInt(e.target.value)
            setYear(y)
            setCertNumber(prev => prev.replace(/MB\d+/, `MB${y + 543}`))
          }}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
        <label className="text-sm font-medium text-gray-700">เลขที่:</label>
        <Input
          className="w-52 font-mono"
          value={certNumber}
          onChange={(e) => setCertNumber(e.target.value)}
          placeholder={`MB${toBE(year)}/0001`}
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1a237e] border-t-transparent" />
        </div>
      )}

      {cert && selectedEmployee && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  PND.50 — WHT Certificate
                </CardTitle>
                <CardDescription>หนังสือรับรองการหักภาษี ณ ที่จ่าย ตามมาตรา 50 ทวิ — Year {year}</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={() => {
                if (!selectedEmployee || !cert) return
                generateMailBITPND50({
                  certificateNumber: certNumber || `MB${year + 543}/${selectedEmployee.employee_code}`,
                  year,
                  employeeNameTh: `${selectedEmployee.title_th}${selectedEmployee.first_name_th} ${selectedEmployee.last_name_th}`,
                  employeeName: `${selectedEmployee.first_name_en || selectedEmployee.first_name_th} ${selectedEmployee.last_name_en || selectedEmployee.last_name_th}`,
                  employeeNationalId: selectedEmployee.national_id,
                  employeeAddress: '919/252 Jewelry Trade Center, 20th Floor, Silom Road, Bangkok 10500',
                  totalIncome: cert.total_income,
                  totalTax: cert.total_tax,
                  companyTaxId: selectedEmployee.company_tax_id,
                })
              }}>
                <Printer className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Info */}
            <div className="rounded-lg border border-gray-200 p-4 space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Employer
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <span className="text-gray-500">Company:</span>{' '}
                  <span className="font-medium">MailBIT Co., Ltd.</span>
                </div>
                <div>
                  <span className="text-gray-500">Tax ID:</span>{' '}
                  <span className="font-mono">{selectedEmployee.company_tax_id}</span>
                </div>
              </div>
            </div>

            {/* Employee Info */}
            <div className="rounded-lg border border-gray-200 p-4 space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <User className="h-4 w-4" />
                Employee
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>{' '}
                  <span className="font-medium">
                    {cert.employee.title_th}{cert.employee.first_name_th} {cert.employee.last_name_th}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">National ID:</span>{' '}
                  <span className="font-mono">{cert.employee.national_id}</span>
                </div>
                {cert.employee.first_name_en && (
                  <div>
                    <span className="text-gray-500">Name (EN):</span>{' '}
                    <span className="font-medium">
                      {cert.employee.first_name_en} {cert.employee.last_name_en}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Department:</span>{' '}
                  <span className="font-medium">{cert.employee.department}</span>
                </div>
              </div>
            </div>

            {/* Monthly Breakdown */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Monthly Breakdown</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Income</TableHead>
                    <TableHead className="text-right">Tax Withheld</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cert.months.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-gray-500 py-4">
                        No monthly data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {cert.months.map((m: any) => (
                        <TableRow key={m.month}>
                          <TableCell>
                            {MONTHS_EN[m.month - 1]}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(m.income)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(m.tax)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals */}
                      <TableRow className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                        <TableCell className="font-bold">Total</TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(cert.total_income)}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(cert.total_tax)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// SSO Report Tab — Social Security Contributions
// ---------------------------------------------------------------------------

function SSOTab() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const [year, setYear] = useState(currentYear)
  const [month, setMonth] = useState(currentMonth)
  const [isExporting, setIsExporting] = useState(false)

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  const { data: response, isLoading } = useQuery({
    queryKey: ['report-sso', year, month],
    queryFn: () => api.get<any>(`/reports/sso/${year}/${month}`),
  })

  const raw = response?.data
  const apiError = response && !response.success ? response.error : null
  const entries: any[] = raw?.entries ?? []

  const totalSalary = entries.reduce((sum: number, e: any) => sum + (e.base_salary || 0), 0)
  const totalSS = entries.reduce((sum: number, e: any) => sum + (e.social_security || 0), 0)
  const totalSSEr = entries.reduce((sum: number, e: any) => sum + (e.social_security_employer || 0), 0)

  const handleExportXLSX = async () => {
    setIsExporting(true)
    try {
      const annualResponse = await api.get<any>(`/reports/sso/${year}`)
      if (!annualResponse.success || !annualResponse.data) {
        alert('No SSO data available for ' + year)
        return
      }

      const { months: monthlyData } = annualResponse.data
      const XLSX = await import('xlsx')
      const wb = XLSX.utils.book_new()

      const sheetNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      let hasAnyData = false

      for (let m = 1; m <= 12; m++) {
        const monthEntries = monthlyData[m]
        if (!monthEntries || monthEntries.length === 0) continue
        hasAnyData = true

        const rows: (string | number)[][] = [
          [`SSO Report (สปส. 1-10) — ${MONTHS_EN[m - 1]} ${year}`],
          [],
          ['No.', 'National ID', 'Title', 'First Name', 'Last Name', 'Salary (THB)', 'Employee SS (THB)', 'Employer SS (THB)'],
        ]

        let mTotalSalary = 0
        let mTotalSS = 0
        let mTotalSSEr = 0

        monthEntries.forEach((entry: any, idx: number) => {
          const salary = entry.base_salary || 0
          const ss = entry.social_security || 0
          const ssEr = entry.social_security_employer || 0
          mTotalSalary += salary
          mTotalSS += ss
          mTotalSSEr += ssEr
          rows.push([
            idx + 1,
            entry.national_id || '',
            entry.title_th || '',
            entry.first_name_th || '',
            entry.last_name_th || '',
            salary,
            ss,
            ssEr,
          ])
        })

        rows.push([])
        rows.push(['', '', '', '', 'TOTAL', mTotalSalary, mTotalSS, mTotalSSEr])

        const ws = XLSX.utils.aoa_to_sheet(rows)
        ws['!cols'] = [
          { wch: 5 },
          { wch: 16 },
          { wch: 12 },
          { wch: 20 },
          { wch: 22 },
          { wch: 14 },
          { wch: 16 },
          { wch: 16 },
        ]
        XLSX.utils.book_append_sheet(wb, ws, sheetNames[m - 1])
      }

      if (!hasAnyData) {
        alert('No SSO data available for ' + year)
        return
      }

      XLSX.writeFile(wb, `SSO-${year}.xlsx`)
    } catch (err) {
      console.error('Failed to export XLSX:', err)
      alert('Failed to export XLSX')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Year:</label>
        <Select className="w-28" value={year.toString()} onChange={(e) => setYear(parseInt(e.target.value))}>
          {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
        </Select>
        <label className="text-sm font-medium text-gray-700">Month:</label>
        <Select className="w-44" value={month.toString()} onChange={(e) => setMonth(parseInt(e.target.value))}>
          {MONTHS_EN.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
        </Select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1a237e] border-t-transparent" />
        </div>
      )}

      {!isLoading && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  SSO Report — Social Security Contributions
                </CardTitle>
                <CardDescription>
                  {MONTHS_EN[month - 1]} {year} — สปส. 1-10 Social Security Fund
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={handleExportXLSX} disabled={isExporting}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                {isExporting ? 'Exporting...' : `Export SSO-${year}.xlsx`}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Employee Name</TableHead>
                  <TableHead>National ID</TableHead>
                  <TableHead className="text-right">Salary</TableHead>
                  <TableHead className="text-right">Employee SS</TableHead>
                  <TableHead className="text-right">Employer SS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className={`text-center py-8 ${apiError ? 'text-red-500' : 'text-gray-500'}`}>
                      {apiError || `No SSO data for ${MONTHS_EN[month - 1]} ${year}. SS-exempt employees are excluded.`}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {entries.map((entry: any, i: number) => {
                      const name = entry.first_name_en && entry.last_name_en
                        ? `${entry.first_name_en} ${entry.last_name_en}`
                        : `${entry.title_th || ''}${entry.first_name_th || ''} ${entry.last_name_th || ''}`
                      return (
                        <TableRow key={i}>
                          <TableCell className="text-gray-500">{i + 1}</TableCell>
                          <TableCell className="font-medium">{name}</TableCell>
                          <TableCell className="font-mono text-sm">{entry.national_id || '-'}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.base_salary)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.social_security)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.social_security_employer)}</TableCell>
                        </TableRow>
                      )
                    })}
                    <TableRow className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                      <TableCell colSpan={3} className="font-bold">
                        Total ({entries.length} employees)
                      </TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalSalary)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalSS)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(totalSSEr)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Reports Page (Main Export)
// ---------------------------------------------------------------------------

export default function ReportsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="h-6 w-6" />
          Reports
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Payroll summaries, tax filings, and export tools
        </p>
      </div>

      <Tabs defaultValue="monthly-summary">
        <TabsList className="flex-wrap">
          <TabsTrigger value="monthly-summary" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Monthly Summary
          </TabsTrigger>
          <TabsTrigger value="quickbooks" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            QuickBooks JV
          </TabsTrigger>
          <TabsTrigger value="pnd1" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            PND.1 Monthly Tax
          </TabsTrigger>
          <TabsTrigger value="pnd1kor" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            PND.50 Annual
          </TabsTrigger>
          <TabsTrigger value="wht-cert" className="flex items-center gap-2">
            <FileDown className="h-4 w-4" />
            PND.50
          </TabsTrigger>
          <TabsTrigger value="sso" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            SSO Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="monthly-summary">
          <ErrorBoundary><MonthlySummaryTab /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="quickbooks">
          <ErrorBoundary><QuickBooksTab /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="pnd1">
          <ErrorBoundary><PND1Tab /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="pnd1kor">
          <ErrorBoundary><PND1KorTab /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="wht-cert">
          <ErrorBoundary><WHTCertificateTab /></ErrorBoundary>
        </TabsContent>

        <TabsContent value="sso">
          <ErrorBoundary><SSOTab /></ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  )
}
